import { TrackJS } from 'trackjs';
import get from 'lodash/get';
import includes from 'lodash/includes';

import { TransferConfig } from '../../config/transfer-config';

import TransferError from '@app/helper/error';
import TransferErrorMessages from '@app/helper/error/messages';

import retriableRequest from '@module/http/axios/retriable-request';
import { HttpResponseCodes } from '@module/http/axios/response';

export const TransferAPI = {
  retriableRequest(method, path, params = null, options = {}) {
    const retries = get(
      options,
      'retries.limit',
      TransferConfig.api.retry.limit
    );

    return retriableRequest({
      path: `${TransferConfig.api.endpoint}${path}`,
      method,
      retries,
      timeout: TransferConfig.api.timeout,
      params,
    });
  },

  async fetchFileObjectId(transferId, params) {
    try {
      const response = await this.retriableRequest(
        'POST',
        `/transfers/${transferId}/files`,
        params,
        {
          retries: {
            limit: TransferConfig.upload.retry.limit,
          },
        }
      );
      return response.body.id;
    } catch (error) {
      switch (error.status) {
        case HttpResponseCodes.BAD_REQUEST:
          throw new TransferError(TransferErrorMessages.INVALID_REQUEST);
        case HttpResponseCodes.FORBIDDEN:
          throw new TransferError(TransferErrorMessages.ACCESS_DENIED);
        default:
          throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
      }
    }
  },

  async fetchPartPutUrl(transferId, fileObjectId, params) {
    try {
      const response = await this.retriableRequest(
        'POST',
        `/transfers/${transferId}/files/${fileObjectId}/part-put-url`,
        params
      );
      return response.body;
    } catch (error) {
      if (includes(error.message, 'timeout')) {
        throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
      }

      switch (error.status) {
        case HttpResponseCodes.BAD_REQUEST:
          throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
        case HttpResponseCodes.FORBIDDEN:
          throw new TransferError(TransferErrorMessages.ACCESS_DENIED);
        default:
          throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
      }
    }
  },

  async finalizeFileObject(transferId, fileObjectId, params) {
    try {
      const response = await this.retriableRequest(
        'PUT',
        `/transfers/${transferId}/files/${fileObjectId}/finalize-mpp`,
        params
      );
      return response.body;
    } catch (error) {
      switch (error.status) {
        case HttpResponseCodes.BAD_REQUEST:
          throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
        case HttpResponseCodes.FORBIDDEN:
          throw new TransferError(TransferErrorMessages.ACCESS_DENIED);
        case HttpResponseCodes.EXPECTATION_FAILED:
          if (error.body && error.body['missing_chunks']) return error.body;
          break;
        default:
          throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
      }
    }
  },

  async verifyToken(transferId, params) {
    try {
      return await this.retriableRequest(
        'POST',
        `/transfers/${transferId}/verify`,
        params
      );
    } catch {
      throw new TransferError(TransferErrorMessages.WRONG_VERIFICATION_CODE);
    }
  },

  async resendToken(transferId, params) {
    try {
      return await this.retriableRequest(
        'POST',
        `/transfers/${transferId}/resend-verification-code`,
        params
      );
    } catch (error) {
      TrackJS.track(error);
    }
  },

  async fetchTransfer(
    transferId,
    secret,
    recipientId = false,
    password = false,
    retry = true
  ) {
    const requestPath = `/transfers/${transferId}/prepare-download`;
    const options = {};
    const params = {
      security_hash: secret,
    };

    if (recipientId) {
      params.recipient_id = recipientId;
    }

    if (password) {
      params.password = password;
    }

    if (retry === false) {
      options.retries = 1;
    }

    try {
      const response = await this.retriableRequest(
        'POST',
        requestPath,
        params,
        options
      );
      return response.body;
    } catch (error) {
      switch (error.status) {
        case HttpResponseCodes.FORBIDDEN:
          throw new TransferError(TransferErrorMessages.ACCESS_DENIED);
        default:
          throw new TransferError(TransferErrorMessages.UNKNOWN_RESPONSE);
      }
    }
  },

  async requestPreview(
    transferId,
    secret,
    recipientId = false,
    transferItemId = false,
    password = false,
    geometry
  ) {
    let initialPath = `/transfers/${transferId}`;
    const params = {
      security_hash: secret,
    };

    // Do we want to fetch a download for single item or entire transfer?
    if (transferItemId !== false) {
      initialPath = `${initialPath}/files/${transferItemId}`;
    }
    initialPath = `${initialPath}/preview`;

    if (geometry && /\d+x\d+/g.test(geometry)) {
      initialPath = `${initialPath}?geometry=${geometry}`;
    }

    if (recipientId) {
      params.recipient_id = recipientId;
    }

    if (password) {
      params.password = password;
    }

    try {
      const response = await this.retriableRequest('POST', initialPath, params);
      return response.body;
    } catch (e) {
      throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
    }
  },

  async fetchTransferDownloadLink({
    transferId,
    secret,
    recipientId = false,
    fileIds = false,
    password = false,
    domainUserId = false,
  }) {
    const path = `/transfers/${transferId}/download`;
    const params = {
      security_hash: secret,
    };

    if (recipientId) {
      params.recipient_id = recipientId;
    }

    if (password) {
      params.password = password;
    }

    if (fileIds) {
      params.file_ids = fileIds;
    }

    if (domainUserId) {
      params.domain_user_id = domainUserId;
    }

    try {
      const response = await this.retriableRequest('POST', path, params);
      return response.body;
    } catch (error) {
      switch (error.status) {
        case HttpResponseCodes.TOO_MANY_REQUESTS:
          throw new TransferError(TransferErrorMessages.TOO_MANY_REQUESTS);
        default:
          throw new TransferError(TransferErrorMessages.INVALID_TRANSFER);
      }
    }
  },
};
